/**
 * Logger Utility
 *
 * Provides a convenient API for logging with Logfire integration.
 * In development: logs to console + Logfire (dual logging for debugging)
 * In production: logs only to Logfire (except errors always log to console)
 *
 * Supports structured attributes for better search/filtering in Logfire dashboard.
 */

import { logfire, isLogfireEnabled } from "./logfire";

/**
 * Structured attributes for log entries
 * All values must be JSON-serializable
 */
export type LogAttributes = Record<string, string | number | boolean | null | undefined>;

/**
 * Format attributes for console output
 */
function formatAttributesForConsole(attributes?: LogAttributes): string {
  if (!attributes || Object.keys(attributes).length === 0) {
    return "";
  }
  return JSON.stringify(attributes, null, 2);
}

/**
 * Logger with severity levels and structured logging
 */
export const logger = {
  /**
   * Trace level - most verbose, for detailed debugging
   * Example: Function entry/exit, detailed state changes
   */
  trace: (message: string, attributes?: LogAttributes) => {
    if (import.meta.env.DEV) {
      const attrs = formatAttributesForConsole(attributes);
      console.log(`[TRACE] ${message}`, attrs || "");
    }
    if (isLogfireEnabled()) {
      logfire.trace(message, attributes);
    }
  },

  /**
   * Debug level - diagnostic information
   * Example: Tool routing, validation steps, state inspection
   */
  debug: (message: string, attributes?: LogAttributes) => {
    if (import.meta.env.DEV) {
      const attrs = formatAttributesForConsole(attributes);
      console.log(`[DEBUG] ${message}`, attrs || "");
    }
    if (isLogfireEnabled()) {
      logfire.debug(message, attributes);
    }
  },

  /**
   * Info level - general informational messages
   * Example: Operation started, operation completed, state transitions
   */
  info: (message: string, attributes?: LogAttributes) => {
    if (import.meta.env.DEV) {
      const attrs = formatAttributesForConsole(attributes);
      console.log(`[INFO] ${message}`, attrs || "");
    }
    if (isLogfireEnabled()) {
      logfire.info(message, attributes);
    }
  },

  /**
   * Notice level - normal but significant events
   * Example: User actions, important state changes
   */
  notice: (message: string, attributes?: LogAttributes) => {
    if (import.meta.env.DEV) {
      const attrs = formatAttributesForConsole(attributes);
      console.log(`[NOTICE] ${message}`, attrs || "");
    }
    if (isLogfireEnabled()) {
      logfire.notice(message, attributes);
    }
  },

  /**
   * Warn level - warning messages
   * Example: Deprecated usage, recoverable errors, unexpected but handled situations
   */
  warn: (message: string, attributes?: LogAttributes) => {
    // Always log warnings to console (even in production)
    const attrs = formatAttributesForConsole(attributes);
    console.warn(`[WARN] ${message}`, attrs || "");

    if (isLogfireEnabled()) {
      logfire.warning(message, attributes);
    }
  },

  /**
   * Error level - error conditions
   * Supports both Error objects and structured attributes
   *
   * @param message - Error description
   * @param errorOrAttributes - Error object, or attributes if no Error
   * @param attributes - Additional attributes (when first param is Error)
   */
  error: (
    message: string,
    errorOrAttributes?: Error | LogAttributes,
    attributes?: LogAttributes
  ) => {
    // Determine if we have an Error object
    const isError = errorOrAttributes instanceof Error;
    const error = isError ? errorOrAttributes : undefined;
    const attrs = isError ? attributes : (errorOrAttributes ?? attributes);

    // Always log errors to console (even in production)
    if (error) {
      console.error(`[ERROR] ${message}`, error, formatAttributesForConsole(attrs));
    } else {
      console.error(`[ERROR] ${message}`, formatAttributesForConsole(attrs));
    }

    if (isLogfireEnabled()) {
      if (error) {
        // Use reportError for Error objects (captures stack trace)
        logfire.reportError(message, error, attrs);
      } else {
        // Use regular error log for messages without Error objects
        logfire.error(message, attrs);
      }
    }
  },

  /**
   * Fatal level - critical errors that may cause application failure
   * Example: Unrecoverable errors, critical system failures
   */
  fatal: (message: string, error?: Error, attributes?: LogAttributes) => {
    // Always log fatal errors to console
    if (error) {
      console.error(`[FATAL] ${message}`, error, formatAttributesForConsole(attributes));
    } else {
      console.error(`[FATAL] ${message}`, formatAttributesForConsole(attributes));
    }

    if (isLogfireEnabled()) {
      if (error) {
        logfire.reportError(message, error, { ...attributes, severity: "fatal" });
      } else {
        logfire.fatal(message, attributes);
      }
    }
  },

  /**
   * Create a span for performance tracking
   * Wraps a callback function and measures its execution time
   *
   * Example:
   * ```ts
   * const result = await logger.span('tool_execution', async () => {
   *   return await executeTool(toolName, args);
   * }, { toolName, threadId });
   * ```
   */
  span: async <T>(
    name: string,
    callback: () => T | Promise<T>,
    attributes?: LogAttributes
  ): Promise<T> => {
    const startTime = performance.now();

    try {
      // Execute callback with Logfire span if enabled
      let result: T;
      if (isLogfireEnabled()) {
        result = await new Promise<T>((resolve, reject) => {
          logfire.span(name, {
            callback: async () => {
              try {
                const r = await callback();
                resolve(r);
              } catch (err) {
                reject(err);
              }
            },
            ...attributes,
          });
        });
      } else {
        result = await callback();
      }

      const duration = performance.now() - startTime;

      if (import.meta.env.DEV) {
        console.log(`[SPAN] ${name} completed in ${duration.toFixed(2)}ms`, attributes || "");
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      if (import.meta.env.DEV) {
        console.error(`[SPAN] ${name} failed after ${duration.toFixed(2)}ms`, error);
      }

      throw error;
    }
  },
};

/**
 * Convenience method to log with component context
 * Automatically adds 'component' attribute
 *
 * Example:
 * ```ts
 * const log = componentLogger('useThreadChat');
 * log.info('Creating chat instance', { threadId });
 * ```
 */
export function componentLogger(componentName: string) {
  return {
    trace: (message: string, attributes?: LogAttributes) =>
      logger.trace(message, { ...attributes, component: componentName }),
    debug: (message: string, attributes?: LogAttributes) =>
      logger.debug(message, { ...attributes, component: componentName }),
    info: (message: string, attributes?: LogAttributes) =>
      logger.info(message, { ...attributes, component: componentName }),
    notice: (message: string, attributes?: LogAttributes) =>
      logger.notice(message, { ...attributes, component: componentName }),
    warn: (message: string, attributes?: LogAttributes) =>
      logger.warn(message, { ...attributes, component: componentName }),
    error: (
      message: string,
      errorOrAttributes?: Error | LogAttributes,
      attributes?: LogAttributes
    ) => logger.error(message, errorOrAttributes, { ...attributes, component: componentName }),
    fatal: (message: string, error?: Error, attributes?: LogAttributes) =>
      logger.fatal(message, error, { ...attributes, component: componentName }),
    span: <T>(name: string, callback: () => T | Promise<T>, attributes?: LogAttributes) =>
      logger.span(name, callback, { ...attributes, component: componentName }),
  };
}
